import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import LeadInActivitiesExercise from './LeadInActivitiesExercise';
import './LeadInActivitiesEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const LEAD_IN_ACTIVITIES_ICON = (
  <span className="default-icon-spacing embed-icon">
    <svg width="28" height="28" viewBox="0 0 48 48" fill="white">
      <g>
        <path d="m25 7.03c0-.552-.447-1-1-1-5.514 0-10 4.486-10 10 0 .552.447 1 1 1s1-.448 1-1c0-4.411 3.589-8 8-8 .553 0 1-.448 1-1z"/>
        <path d="m22.246 45.79h1.754 1.754c1.652 0 2.999-1.345 3-3v-1.099c1.032-.475 1.755-1.512 1.755-2.721v-3.72c.176-.382.281-.802.281-1.25 0-1.016.226-2 .671-2.927.441-.919 1.086-1.752 1.864-2.409 3.746-3.165 5.709-7.989 5.25-12.909-.7-7.375-6.813-13.189-14.265-13.525l-.31-.01-.354.011c-7.408.335-13.521 6.149-14.222 13.526-.458 4.917 1.505 9.742 5.251 12.906.778.658 1.423 1.491 1.864 2.41.445.927.671 1.911.671 2.927 0 .447.105.868.281 1.25v3.721c0 1.209.722 2.247 1.755 2.721v1.1c.001 1.653 1.348 2.998 3 2.998zm4.508-3c0 .552-.449 1-1 1h-1.754-1.754c-.551 0-1-.449-1-1v-.82h2.754 2.754zm1.755-3.819c0 .551-.448 1-1 1h-3.509-3.509c-.552 0-1-.449-1-1v-2.068c.232.058.47.097.719.097h3.79 3.79c.249 0 .487-.039.719-.097zm-9.299-4.971c0-1.318-.292-2.595-.868-3.793-.563-1.171-1.385-2.233-2.376-3.071-3.247-2.742-4.948-6.926-4.551-11.191.607-6.389 5.903-11.426 12.275-11.715l.31-.01.265.009c6.417.29 11.713 5.327 12.319 11.714.398 4.267-1.303 8.451-4.55 11.193-.991.838-1.813 1.9-2.376 3.071-.576 1.198-.868 2.475-.868 3.793 0 .801.111 1.582.304 2.332h7.392c.193-.75.304-1.531.304-2.332z"/>
      </g>
    </svg>
  </span>
);

const LeadInActivitiesEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState } = useExerciseSync(exerciseId);
  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps
  const { activities, title, level, text } = node.attrs;

  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      const isStudentDashboard = typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  if (!activities || !Array.isArray(activities)) {
    return (
      <NodeViewWrapper>
        <div className="lead-in-activities-embed-error">
          <p>Error: Invalid Lead In Activities exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  // Allow placing cursor above/below the embed (like ABCD)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .insertContentAt(pos, { type: 'paragraph' })
      .setTextSelection(pos + 1)
      .run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };


  return (
    <NodeViewWrapper className="lead-in-activities-embed" contentEditable={false} draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="lead-in-activities-exercise-embed" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {!isExpanded ? (
          <div className="embed-preview unified-exercise-preview" draggable={true} data-drag-handle onClick={handleToggle} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {LEAD_IN_ACTIVITIES_ICON}
            <div className="unified-preview-main">
              <div className="unified-preview-title">{title || 'Lead In Activities'}</div>
              <div className="unified-preview-meta">
                {activities.length} activities · Level: {level || 'B1'}
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="embed-expanded" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <LeadInActivitiesExercise
              activities={activities}
              title={title}
              level={level}
              text={text}
              onClose={handleClose}
              updateAttributes={updateAttributes}
              node={node}
              editor={editor}
              getPos={getPos}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default LeadInActivitiesEmbed;